import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useCsvExport } from 'lib/hooks/useCsvExport.tsx'
import { useAtomValue } from 'jotai'
import { selectedCustomPropertyColumnsAtom } from 'modules/Customers/customers.atoms'

const CUSTOM_PROPERTY_PARAM_PREFIX = 'customProperties.'

type UseCustomersCsvExport = () => {
  download: {
    available: boolean
    disabled: boolean
    onClick: (queryParams: URLSearchParams) => void
  }
}

const customPropertyFiltersFrom = (queryParams: URLSearchParams) =>
  Object.fromEntries(
    [...queryParams.entries()]
      .filter(([param]) => param.startsWith(CUSTOM_PROPERTY_PARAM_PREFIX))
      .map(([param, value]) => [
        param.slice(CUSTOM_PROPERTY_PARAM_PREFIX.length),
        value
      ])
  )

const billingStatusFrom = (queryParams: URLSearchParams) => {
  const billingStatus = queryParams.get('billingStatus')
  return billingStatus === 'ACTIVE' || billingStatus === 'INACTIVE'
    ? billingStatus
    : undefined
}

/**
 * Takes the list API query params of the customers table, so the export
 * returns the same customers as the table.
 */
export const useCustomersCsvExport: UseCustomersCsvExport = () => {
  const customPropertyKeys = useAtomValue(selectedCustomPropertyColumnsAtom)

  return useCsvExport({
    getExport: queryParams => {
      const customPropertyFilters = customPropertyFiltersFrom(queryParams)

      return dashboard20240730Client.getCustomersCsvDownload({
        legalName: queryParams.get('legalName') ?? undefined,
        email: queryParams.get('email') ?? undefined,
        alias: queryParams.get('alias') ?? undefined,
        label: queryParams.get('label') ?? undefined,
        billingStatus: billingStatusFrom(queryParams),
        includeArchived: queryParams.get('includeArchived') === 'true',
        includeCustomProperties:
          customPropertyKeys.length > 0 ? customPropertyKeys : undefined,
        customPropertyFilters:
          Object.keys(customPropertyFilters).length > 0
            ? customPropertyFilters
            : undefined
      })
    },
    fileName: 'customers-export.csv'
  })
}
